// De openbare leeslink van een plan (Airtable 21, state 13 en het blok Delen).
//
// Aan of uit. Staat hij aan, dan leest iedereen met de link de laatst gedeelde
// versie, zonder inlog. "Nieuwe link maken" laat de oude meteen stoppen: zo maak
// je doorsturen ongedaan. Dit blok staat in Delen (AM en BP) en op de pagina van
// het plan in Beheer; de server beslist wie het mag (contract plan-pagina-v1).

import { Copy, RefreshCw } from "lucide-react";
import { useEffect, useState } from "react";

import type { PlanLeeslink } from "../../contracts";
import { ApiError, api } from "../../lib/api";
import { Foutmelding, Invoer, Knop, Notitie, Schakelaar } from "../../stijl";

export function leeslinkAdres(link: PlanLeeslink): string {
  return `${window.location.origin}${link.pad}`;
}

export function LeeslinkBlok({
  plan,
  toon,
  begin,
  onVerander,
}: {
  plan: { propositie: string; jaar: number };
  toon?: (t: string) => void;
  /** Wat de pagina al weet (Beheer laadt hem mee); anders haalt het blok hem op. */
  begin?: PlanLeeslink | null;
  /** Na aan, uit of een nieuwe link: zodat de pagina eromheen bijwerkt. */
  onVerander?: () => void;
}) {
  const [link, setLink] = useState<PlanLeeslink | null | undefined>(begin);
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState<string | null>(null);

  useEffect(() => {
    if (begin !== undefined) {
      setLink(begin);
      return;
    }
    let weg = false;
    api.planLeeslink(plan.propositie, plan.jaar).then(
      (r) => !weg && setLink(r.leeslink),
      (e: unknown) => {
        if (weg) return;
        setLink(null);
        setFout(e instanceof ApiError ? e.message || e.reason : "De leeslink is niet op te halen.");
      },
    );
    return () => {
      weg = true;
    };
  }, [plan.propositie, plan.jaar, begin]);

  const doe = async (wat: () => Promise<{ leeslink: PlanLeeslink | null }>, gelukt: string) => {
    setBezig(true);
    setFout(null);
    try {
      const r = await wat();
      setLink(r.leeslink);
      toon?.(gelukt);
      onVerander?.();
    } catch (e) {
      setFout(e instanceof ApiError ? e.message || e.reason : "Dat is niet gelukt.");
    } finally {
      setBezig(false);
    }
  };
  const aan = () => doe(() => api.maakPlanLeeslink(plan.propositie, plan.jaar), "De leeslink staat aan.");
  const uit = () => doe(() => api.zetPlanLeeslinkUit(plan.propositie, plan.jaar), "De leeslink staat uit.");
  const nieuw = () => {
    if (!window.confirm("Nieuwe link maken? De oude link werkt daarna meteen niet meer.")) return;
    void doe(() => api.maakPlanLeeslink(plan.propositie, plan.jaar), "Nieuwe link gemaakt. De oude werkt niet meer.");
  };
  const kopieer = (adres: string) => {
    const mis = () => {
      (document.getElementById("p-leeslink") as HTMLInputElement | null)?.select();
      toon?.("Selecteer en kopieer de link.");
    };
    try {
      void navigator.clipboard.writeText(adres).then(() => toon?.("Leeslink gekopieerd."), mis);
    } catch {
      mis();
    }
  };

  if (link === undefined) return <p className="s-veldblok__uitleg">Leeslink laden…</p>;
  return (
    <div className="s-veldblok" data-lat="leeslink">
      <span className="s-lbl">Leeslink voor buiten Summa</span>
      <Schakelaar
        aan={!!link}
        onWissel={(nu) => {
          if (bezig) return;
          void (nu ? aan() : uit());
        }}
        titel={link ? "Leeslink staat aan · iedereen met de link kan lezen" : "Leeslink staat uit"}
        uitleg={
          link
            ? "Zonder inlog. De link toont de laatst gedeelde versie, zonder namen van collega's en zonder punten."
            : "Zet hem aan voor een werkgever: die leest dan zonder inlog de laatst gedeelde versie."
        }
      />
      {link && (
        <>
          <div className="s-invoerrij">
            <Invoer
              id="p-leeslink"
              readOnly
              value={leeslinkAdres(link)}
              aria-label="Leeslink"
              onFocus={(e) => e.currentTarget.select()}
            />
            <Knop variant="zacht" icoon={Copy} onClick={() => kopieer(leeslinkAdres(link))}>
              Kopieer
            </Knop>
          </div>
          <div className="s-invoerrij">
            <Knop icoon={RefreshCw} disabled={bezig} onClick={nieuw}>
              Nieuwe link maken
            </Knop>
            <Knop disabled={bezig} onClick={() => void uit()}>
              Zet uit
            </Knop>
          </div>
          <Notitie>"Nieuwe link maken" laat de oude link meteen stoppen. Zo maak je doorsturen ongedaan.</Notitie>
        </>
      )}
      {fout && <Foutmelding>{fout}</Foutmelding>}
    </div>
  );
}
